// Reusable status indicator for any v2 blockchain write — single-step
// (e.g. voucher mint) or multi-step (e.g. spend-plan create = account +
// envelope + funding, monthly cycle = step 1..4).
//
// Two usage modes:
//
//   1. Single-step (legacy): pass `success` / `failure` / `pending` /
//      `notApplicable`. Pill shows a single state (On-chain / Off-chain /
//      Pending / Awaiting). Hover reveals a Re-sync verb. Click → onRetry.
//
//   2. Multi-step (preferred for compound writes): pass `steps[]`. Each
//      step carries its own label, status, tooltip, optional tx hash,
//      explorer URL, error message, and optional per-step `onRetry`.
//      The rollup pill shows progress ("2/3", "Done", "1 failed").
//      Click opens a Popover listing every step with per-step
//      icons, tooltips, hash links, and retry buttons.
//
// The popover is the truthful breakdown — the pill is just the at-a-glance
// rollup. This matters for cases like QA spend plans that were created
// before the blockchain integration was wired up: the popover can explain
// "envelope was never created — this plan predates on-chain integration"
// while the pill simply reads "0/3 — off chain".

import { useMemo, useRef, useState, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
	Box,
	ButtonBase,
	CircularProgress,
	Divider,
	IconButton,
	Link as MuiLink,
	Popover,
	Stack,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/ScheduleOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { truncateHash } from '../../../utils/strings';

export type OnChainStepStatus = 'success' | 'pending' | 'failed' | 'notApplicable';

export interface OnChainFailure {
	id?: string | number;
	error?: string;
}

export interface OnChainStep {
	/** Stable identifier for React keys + retry dispatch. */
	key: string;
	/** Short user-facing name, e.g. "Create account", "Fund envelope". */
	label: string;
	status: OnChainStepStatus;
	/** Longer prose shown in the popover under the label. */
	description?: string;
	/** Tooltip on the step's status icon — used for failure messages,
	 *  "awaiting prior step", etc. */
	tooltip?: string;
	/** On-chain tx hash if the step landed. */
	txHash?: string | null;
	/** Block-explorer URL for the tx (renders a hash link in the popover). */
	explorerUrl?: string;
	/** Raw error string from `blk.blockchain_failure.error_message`. */
	errorMessage?: string;
	/** Per-step retry. When omitted, the step row is read-only and the
	 *  badge falls back to the top-level `onRetry` for that step. */
	onRetry?: () => Promise<void> | void;
}

export interface OnChainStatusBadgeProps {
	// ---------- Single-step inputs (ignored when `steps` is provided) -----
	/** Transaction hash if the write succeeded on chain. */
	txHash?: string | null;
	/** Explicit success override for callers that don't have a tx hash. */
	success?: boolean;
	/** Failure record from `blk.blockchain_failure`. */
	failure?: OnChainFailure | null;
	/** Force the pending spinner regardless of hash/failure state. */
	pending?: boolean;
	/** Block-explorer URL for the single-step tx hash. */
	explorerUrl?: string;

	// ---------- Multi-step input -----------------------------------------
	/** Multi-step breakdown. When supplied, the pill shows progress and
	 *  click opens a popover detailing every step. */
	steps?: OnChainStep[];

	// ---------- Lifecycle / messaging overrides --------------------------
	/** Marks the badge as "not yet applicable" — neutral grey, no retry.
	 *  Pass a string to supply the tooltip; pass `true` for default copy. */
	notApplicable?: boolean | string;
	/** Label override for the not-applicable state. */
	notApplicableLabel?: string;
	/** Tooltip override for the rollup pill (any status). */
	tooltip?: string;
	/** Action verb override (default: "Re-sync"/"Re-verify"/"Re-check"). */
	actionLabel?: string;

	// ---------- Layout ---------------------------------------------------
	/** Caption shown to the left of the pill. */
	label?: string;
	/** Compact mode: never render the trailing hash text. */
	compact?: boolean;

	// ---------- Retry ----------------------------------------------------
	/** Re-run the underlying SDK call(s). Idempotent server-side. In
	 *  multi-step mode this is the "Re-sync all" handler at the top of the
	 *  popover; individual steps can also expose their own `onRetry`. */
	onRetry?: () => Promise<void> | void;
}

type RollupStatus = OnChainStepStatus;

// --- Helpers ----------------------------------------------------------------

const rollupFromSteps = (steps: OnChainStep[]): RollupStatus => {
	const effective = steps.filter((s) => s.status !== 'notApplicable');
	if (effective.length === 0) return 'notApplicable';
	if (effective.some((s) => s.status === 'failed')) return 'failed';
	if (effective.some((s) => s.status === 'pending')) return 'pending';
	if (effective.every((s) => s.status === 'success')) return 'success';
	// Mixed success + (nothing pending / nothing failed) shouldn't happen,
	// but treat as failed so the user is prompted to investigate.
	return 'failed';
};

const resolveSingleStatus = ({
	txHash,
	success,
	failure,
	pending,
	notApplicable,
}: Pick<
	OnChainStatusBadgeProps,
	'txHash' | 'success' | 'failure' | 'pending' | 'notApplicable'
>): RollupStatus => {
	if (notApplicable) return 'notApplicable';
	if (txHash || success) return 'success';
	if (failure) return 'failed';
	if (pending) return 'pending';
	return 'failed';
};

// --- Component --------------------------------------------------------------

export const OnChainStatusBadge = ({
	txHash,
	success,
	failure,
	pending,
	explorerUrl,
	steps,
	notApplicable,
	notApplicableLabel,
	tooltip,
	actionLabel,
	label,
	compact = false,
	onRetry,
}: OnChainStatusBadgeProps) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [retrying, setRetrying] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [popoverOpen, setPopoverOpen] = useState(false);
	const anchorRef = useRef<HTMLButtonElement | null>(null);

	const hasSteps = Array.isArray(steps) && steps.length > 0;

	// `notApplicable` prop always wins (lifecycle gate); otherwise rollup
	// from steps if provided, else fall back to single-step inputs.
	const status: RollupStatus = useMemo(() => {
		if (notApplicable) return 'notApplicable';
		if (hasSteps) return rollupFromSteps(steps as OnChainStep[]);
		return resolveSingleStatus({ txHash, success, failure, pending, notApplicable });
	}, [notApplicable, hasSteps, steps, txHash, success, failure, pending]);

	// Progress counts for multi-step pill copy. `total` is the count of
	// steps the chain *will* try to run (excludes notApplicable so the
	// rollup doesn't say "0/1" when the only step is still gated by an
	// earlier lifecycle event). `raw` is the unfiltered count, used by the
	// popover when every step is notApplicable (e.g. DRAFT spend plan).
	const counts = useMemo(() => {
		if (!hasSteps) return null;
		const all = steps as OnChainStep[];
		const effective = all.filter((s) => s.status !== 'notApplicable');
		return {
			total: effective.length,
			raw: all.length,
			success: effective.filter((s) => s.status === 'success').length,
			failed: effective.filter((s) => s.status === 'failed').length,
			pending: effective.filter((s) => s.status === 'pending').length,
			notYet: all.length - effective.length,
		};
	}, [hasSteps, steps]);

	const handleRetry = async (e?: MouseEvent) => {
		e?.stopPropagation();
		e?.preventDefault();
		if (!onRetry || retrying) return;
		setRetrying(true);
		try {
			await onRetry();
		} finally {
			setRetrying(false);
		}
	};

	// --- Per-status visual config + default copy ----------------------------
	const cfg = (() => {
		if (status === 'notApplicable') {
			const defaultTip = t(
				'blockchain.status.notApplicableTooltip',
				'The on-chain commitment runs once this is approved.'
			);
			return {
				bg: alpha(theme.palette.text.primary, 0.06),
				fg: theme.palette.text.secondary,
				accent: theme.palette.text.secondary,
				icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
				defaultLabel:
					notApplicableLabel || t('blockchain.status.notApplicable', 'Not on chain yet'),
				defaultAction: '',
				defaultTooltip: typeof notApplicable === 'string' ? notApplicable : defaultTip,
			};
		}
		if (status === 'success') {
			return {
				bg: alpha(theme.palette.success.main, 0.12),
				fg: theme.palette.success.dark,
				accent: theme.palette.success.main,
				icon: <CheckCircleIcon sx={{ fontSize: 14 }} />,
				defaultLabel:
					hasSteps && counts
						? counts.total > 1
							? t('blockchain.status.allDone', 'All on chain')
							: t('blockchain.status.onChain', 'On-chain')
						: t('blockchain.status.onChain', 'On-chain'),
				defaultAction: t('blockchain.status.verify', 'Re-verify'),
				defaultTooltip: t(
					'blockchain.status.verifyTooltip',
					'Re-run the on-chain call to confirm state matches.'
				),
			};
		}
		if (status === 'pending') {
			return {
				bg: alpha(theme.palette.warning.main, 0.14),
				fg: theme.palette.warning.dark,
				accent: theme.palette.warning.main,
				icon: <CircularProgress size={10} thickness={5} sx={{ color: 'inherit' }} />,
				defaultLabel:
					hasSteps && counts
						? t('blockchain.status.progress', '{{done}}/{{total}} on chain', {
								done: counts.success,
								total: counts.total,
							})
						: t('blockchain.status.pending', 'Pending'),
				defaultAction: t('blockchain.status.check', 'Re-check'),
				defaultTooltip: t(
					'blockchain.status.pendingTooltip',
					'Awaiting on-chain confirmation. Re-check now.'
				),
			};
		}
		// failed
		return {
			bg: alpha(theme.palette.error.main, 0.12),
			fg: theme.palette.error.dark,
			accent: theme.palette.error.main,
			icon: <ErrorIcon sx={{ fontSize: 14 }} />,
			defaultLabel:
				hasSteps && counts
					? counts.success > 0
						? t('blockchain.status.partial', '{{done}}/{{total}} on chain', {
								done: counts.success,
								total: counts.total,
							})
						: t('blockchain.status.offChain', 'Off-chain')
					: t('blockchain.status.offChain', 'Off-chain'),
			defaultAction: t('blockchain.status.resync', 'Re-sync'),
			defaultTooltip:
				failure?.error ||
				t('blockchain.status.failedTooltip', 'On-chain write did not complete. Click to re-sync.'),
		};
	})();

	const pillLabel = cfg.defaultLabel;
	const pillAction = actionLabel || cfg.defaultAction;
	const pillTooltip = tooltip || cfg.defaultTooltip;

	// Click behavior: popover wins when there are steps; otherwise direct
	// retry. `notApplicable` is always non-interactive.
	// Popover is available whenever steps are supplied, even when the
	// rollup is `notApplicable` (e.g. a DRAFT spend plan) — the popover
	// is how we explain *why* nothing has happened on chain yet.
	const canOpenPopover = hasSteps;
	const canRetryDirect = !hasSteps && !!onRetry && status !== 'notApplicable';
	const isInteractive = canOpenPopover || canRetryDirect;
	const showAction = canRetryDirect && (hovered || retrying);

	const handlePillClick = (e: MouseEvent<HTMLButtonElement>) => {
		if (canOpenPopover) {
			anchorRef.current = e.currentTarget;
			setPopoverOpen(true);
			return;
		}
		if (canRetryDirect) void handleRetry(e);
	};

	const pill = (
		<Tooltip
			title={
				!popoverOpen && (isInteractive || status === 'failed' || status === 'notApplicable')
					? pillTooltip
					: ''
			}
			arrow
			placement="top">
			<ButtonBase
				component={isInteractive ? 'button' : 'span'}
				disabled={!isInteractive || retrying}
				onClick={isInteractive ? handlePillClick : undefined}
				onMouseEnter={() => setHovered(true)}
				onMouseLeave={() => setHovered(false)}
				onFocus={() => setHovered(true)}
				onBlur={() => setHovered(false)}
				focusRipple={isInteractive}
				aria-label={
					canRetryDirect
						? `${pillLabel} — ${pillAction}`
						: canOpenPopover
							? `${pillLabel} — ${t('blockchain.status.showDetails', 'show details')}`
							: pillLabel
				}
				sx={{
					bgcolor: cfg.bg,
					color: cfg.fg,
					fontWeight: 600,
					fontSize: 12,
					lineHeight: 1,
					height: 22,
					borderRadius: 11,
					px: 1,
					display: 'inline-flex',
					alignItems: 'center',
					gap: 0.5,
					cursor: isInteractive ? 'pointer' : 'default',
					transition: theme.transitions.create(['background-color', 'box-shadow'], {
						duration: theme.transitions.duration.short,
					}),
					'&:hover': isInteractive
						? {
								bgcolor: alpha(cfg.accent, 0.2),
								boxShadow: `0 0 0 1px ${alpha(cfg.accent, 0.4)}`,
							}
						: undefined,
					'&.Mui-disabled': { color: cfg.fg, opacity: retrying ? 0.85 : 1 },
				}}>
				{/* Status icon — nudges left when direct-retry action reveals */}
				<Box
					component="span"
					sx={{
						display: 'inline-flex',
						alignItems: 'center',
						color: cfg.accent,
						transform: showAction ? 'translateX(-2px)' : 'translateX(0)',
						transition: theme.transitions.create('transform', {
							duration: theme.transitions.duration.shortest,
						}),
					}}>
					{retrying ? (
						<CircularProgress size={10} thickness={5} sx={{ color: 'inherit' }} />
					) : (
						cfg.icon
					)}
				</Box>

				{/* Default label — collapses when direct-retry action shows */}
				<Box
					component="span"
					sx={{
						maxWidth: showAction ? 0 : 160,
						opacity: showAction ? 0 : 1,
						overflow: 'hidden',
						whiteSpace: 'nowrap',
						transition: theme.transitions.create(['max-width', 'opacity'], {
							duration: theme.transitions.duration.shortest,
						}),
					}}>
					{pillLabel}
				</Box>

				{/* Direct-retry action label (single-step mode only) */}
				{canRetryDirect && (
					<Box
						component="span"
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.25,
							maxWidth: showAction ? 120 : 0,
							opacity: showAction ? 1 : 0,
							overflow: 'hidden',
							whiteSpace: 'nowrap',
							transition: theme.transitions.create(['max-width', 'opacity'], {
								duration: theme.transitions.duration.shortest,
							}),
						}}>
						<RefreshIcon sx={{ fontSize: 12 }} />
						{retrying ? t('blockchain.status.retrying', 'Re-syncing…') : pillAction}
					</Box>
				)}
			</ButtonBase>
		</Tooltip>
	);

	return (
		<>
			<Stack direction="row" alignItems="center" spacing={0.75}>
				{label && (
					<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
						{label}
					</Typography>
				)}
				{pill}

				{/* Hash display — single-step success, non-compact only */}
				{!hasSteps && !compact && status === 'success' && txHash && (
					<Typography
						component={explorerUrl ? 'a' : 'span'}
						{...(explorerUrl ? { href: explorerUrl, target: '_blank', rel: 'noreferrer' } : {})}
						variant="caption"
						sx={{
							fontFamily: 'ui-monospace, SFMono-Regular, monospace',
							color: explorerUrl ? 'primary.main' : 'text.secondary',
							textDecoration: 'none',
							'&:hover': explorerUrl ? { textDecoration: 'underline' } : undefined,
						}}>
						{truncateHash(txHash)}
					</Typography>
				)}
			</Stack>

			{canOpenPopover && (
				<StepsPopover
					open={popoverOpen}
					anchorEl={anchorRef.current}
					onClose={() => setPopoverOpen(false)}
					steps={steps as OnChainStep[]}
					counts={counts}
					rollup={status}
					title={label || t('blockchain.status.breakdown', 'On-chain breakdown')}
					summaryTooltip={tooltip}
					onResyncAll={onRetry && status !== 'notApplicable' ? handleRetry : undefined}
					resyncAllLabel={actionLabel || t('blockchain.status.resyncAll', 'Re-sync all')}
					retryingAll={retrying}
				/>
			)}
		</>
	);
};

// --- Popover ----------------------------------------------------------------

interface StepsPopoverProps {
	open: boolean;
	anchorEl: HTMLElement | null;
	onClose: () => void;
	steps: OnChainStep[];
	counts: {
		total: number;
		raw: number;
		success: number;
		failed: number;
		pending: number;
		notYet: number;
	} | null;
	rollup: RollupStatus;
	title: string;
	summaryTooltip?: string;
	onResyncAll?: () => Promise<void> | void;
	resyncAllLabel: string;
	retryingAll: boolean;
}

const StepsPopover = ({
	open,
	anchorEl,
	onClose,
	steps,
	counts,
	rollup,
	title,
	summaryTooltip,
	onResyncAll,
	resyncAllLabel,
	retryingAll,
}: StepsPopoverProps) => {
	const { t } = useTranslation();
	const theme = useTheme();

	const headerAccent =
		rollup === 'success'
			? theme.palette.success.main
			: rollup === 'pending'
				? theme.palette.warning.main
				: rollup === 'failed'
					? theme.palette.error.main
					: theme.palette.text.secondary;

	return (
		<Popover
			open={open}
			anchorEl={anchorEl}
			onClose={onClose}
			anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			transformOrigin={{ vertical: 'top', horizontal: 'left' }}
			slotProps={{
				paper: {
					sx: {
						width: 360,
						maxWidth: '90vw',
						p: 0,
						borderTop: `3px solid ${headerAccent}`,
					},
				},
			}}>
			<Box sx={{ p: 2, pb: 1.5 }}>
				<Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
					{title}
				</Typography>
				{counts && (
					<Typography variant="caption" color="text.secondary">
						{counts.total === 0
							? t('blockchain.status.summaryAwaiting', '{{n}} step{{plural}} awaiting trigger', {
									n: counts.raw,
									plural: counts.raw === 1 ? '' : 's',
								})
							: t('blockchain.status.summary', '{{done}} of {{total}} on chain', {
									done: counts.success,
									total: counts.total,
								})}
						{counts.failed > 0 &&
							` · ${t('blockchain.status.failedCount', '{{n}} failed', {
								n: counts.failed,
							})}`}
						{counts.pending > 0 &&
							` · ${t('blockchain.status.pendingCount', '{{n}} pending', {
								n: counts.pending,
							})}`}
						{counts.total > 0 &&
							counts.notYet > 0 &&
							` · ${t('blockchain.status.notYetCount', '{{n}} awaiting', {
								n: counts.notYet,
							})}`}
					</Typography>
				)}
				{summaryTooltip && (
					<Typography
						variant="body2"
						color="text.secondary"
						sx={{ mt: 1, fontSize: 12.5, lineHeight: 1.5 }}>
						{summaryTooltip}
					</Typography>
				)}
			</Box>

			<Divider />

			<Stack divider={<Divider flexItem />}>
				{steps.map((step) => (
					<StepRow key={step.key} step={step} />
				))}
			</Stack>

			{onResyncAll && (
				<>
					<Divider />
					<Box sx={{ p: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
						<ButtonBase
							onClick={() => void onResyncAll()}
							disabled={retryingAll}
							sx={{
								fontSize: 12,
								fontWeight: 600,
								color: theme.palette.primary.main,
								px: 1.25,
								py: 0.5,
								borderRadius: 1,
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.5,
								'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
								'&.Mui-disabled': { opacity: 0.6 },
							}}>
							{retryingAll ? (
								<CircularProgress size={12} thickness={5} />
							) : (
								<RefreshIcon sx={{ fontSize: 14 }} />
							)}
							{retryingAll ? t('blockchain.status.retrying', 'Re-syncing…') : resyncAllLabel}
						</ButtonBase>
					</Box>
				</>
			)}
		</Popover>
	);
};

// --- Step row ---------------------------------------------------------------

const StepRow = ({ step }: { step: OnChainStep }) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [retrying, setRetrying] = useState(false);

	const accent =
		step.status === 'success'
			? theme.palette.success.main
			: step.status === 'pending'
				? theme.palette.warning.main
				: step.status === 'failed'
					? theme.palette.error.main
					: theme.palette.text.disabled;

	const icon = (() => {
		if (retrying) return <CircularProgress size={14} thickness={5} sx={{ color: accent }} />;
		switch (step.status) {
			case 'success':
				return <CheckCircleIcon sx={{ fontSize: 18, color: accent }} />;
			case 'pending':
				return <CircularProgress size={14} thickness={5} sx={{ color: accent }} />;
			case 'failed':
				return <ErrorIcon sx={{ fontSize: 18, color: accent }} />;
			default:
				return <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: accent }} />;
		}
	})();

	const handleStepRetry = async () => {
		if (!step.onRetry || retrying) return;
		setRetrying(true);
		try {
			await step.onRetry();
		} finally {
			setRetrying(false);
		}
	};

	return (
		<Box sx={{ p: 1.5, display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
			<Tooltip title={step.tooltip || ''} arrow placement="left">
				<Box
					sx={{
						mt: 0.25,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center',
						width: 20,
						height: 20,
					}}>
					{icon}
				</Box>
			</Tooltip>

			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography variant="body2" sx={{ fontWeight: 600, fontSize: 13 }}>
					{step.label}
				</Typography>

				{step.description && (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{ display: 'block', mt: 0.25, lineHeight: 1.45 }}>
						{step.description}
					</Typography>
				)}

				{step.errorMessage && step.status === 'failed' && (
					<Typography
						variant="caption"
						sx={{
							display: 'block',
							mt: 0.5,
							color: theme.palette.error.dark,
							fontFamily: 'ui-monospace, SFMono-Regular, monospace',
							fontSize: 11.5,
							wordBreak: 'break-word',
						}}>
						{step.errorMessage}
					</Typography>
				)}

				{step.txHash && (
					<MuiLink
						href={step.explorerUrl}
						target={step.explorerUrl ? '_blank' : undefined}
						rel={step.explorerUrl ? 'noreferrer' : undefined}
						underline="hover"
						sx={{
							display: 'inline-flex',
							alignItems: 'center',
							gap: 0.5,
							mt: 0.5,
							fontFamily: 'ui-monospace, SFMono-Regular, monospace',
							fontSize: 11.5,
							pointerEvents: step.explorerUrl ? 'auto' : 'none',
							color: step.explorerUrl ? 'primary.main' : 'text.secondary',
						}}>
						{truncateHash(step.txHash)}
						{step.explorerUrl && <OpenInNewIcon sx={{ fontSize: 11 }} />}
					</MuiLink>
				)}
			</Box>

			{step.onRetry && step.status !== 'success' && step.status !== 'notApplicable' && (
				<Tooltip title={t('blockchain.status.retryStep', 'Retry this step')} arrow placement="top">
					<IconButton size="small" onClick={handleStepRetry} disabled={retrying} sx={{ mt: -0.5 }}>
						{retrying ? (
							<CircularProgress size={14} thickness={5} />
						) : (
							<RefreshIcon sx={{ fontSize: 16 }} />
						)}
					</IconButton>
				</Tooltip>
			)}
		</Box>
	);
};
